import React, { ReactNode, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { CircularProgress, Stack } from '@mui/material';
import { useTimeZone } from 'hooks/useTimeZone';
import { useOptimisticState } from 'hooks/useOptimistic';
import { CalendarEventsPeriod } from '@trnsact/trnsact-shared-types';
import { EventInteractionArgs } from 'react-big-calendar/lib/addons/dragAndDrop';
import { RenderWithFallback } from 'components/shared/Conditional/RenderWithFallback';

import { useEventIdParam } from '../../hooks';
import { CalendarMapper } from '../../mapper';
import { CalendarEventMapper } from './mapper';
import { calendarApi, useCalendar } from '../../api';
import { QueryListResponse } from '../../../../../global';
import { DealActivityAPINameSpace } from '../../../api/types';
import { CalendarDateRange, CalendarEvent, CalendarEventEntity, CalendarViewProps } from './types';

interface Props {
  period: CalendarEventsPeriod;
  dateRange: CalendarDateRange;
  children: (props: CalendarViewProps) => ReactNode;
}

export const CalendarConnector = ({ period, dateRange, children }: Props) => {
  const { openEvent } = useEventIdParam();
  const { handleReDateEvent } = useCalendar();
  const { timeZoneOffsetMinutes, timeZone } = useTimeZone();

  const { data, previousData, error, loading } = useQuery<
    DealActivityAPINameSpace<QueryListResponse<CalendarEventEntity>>
  >(calendarApi.listOfCalendarEvents, {
    variables: {
      input: {
        period,
        timeZone,
        timeZoneOffsetMinutes,
        ...dateRange,
      },
    },
  });

  const eventsList = (data ?? previousData)?.dealActivity?.result?.list || [];

  const serverEvents = CalendarEventMapper.mapToCalendarEvent(eventsList);

  const [events, setEvents] = useOptimisticState<CalendarEvent[]>(serverEvents);

  useEffect(() => {
    setEvents(serverEvents);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [CalendarMapper.getEventsSignature(serverEvents)]);

  const handleSelectEvent = (event: CalendarEvent) => {
    openEvent(event.id);
  };

  const handleEventDrop = async (dto: EventInteractionArgs<CalendarEvent>) => {
    const movedEvents = CalendarMapper.moveEventInList(events, dto);
    const reDateEvent = CalendarMapper.mapDtoToReDate(dto);

    await setEvents(
      handleReDateEvent({ events: [reDateEvent] }, api => ({
        awaitRefetchQueries: true,
        refetchQueries: [api.listOfCalendarEvents],
      })).then(result => (result ? movedEvents : events)),
      movedEvents
    );
  };

  return (
    <RenderWithFallback
      error={error}
      isLoading={loading && !eventsList.length}
      fallBack={
        <Stack alignItems="center" justifyContent="center" height="100%">
          <CircularProgress />
        </Stack>
      }
    >
      {children({ events, onSelectEvent: handleSelectEvent, onEventDrop: handleEventDrop })}
    </RenderWithFallback>
  );
};
